/**
 * usePushNotifications
 *
 * Handles the full FCM web-push lifecycle for an authenticated user:
 *  1. Registers the firebase-messaging-sw.js service worker.
 *  2. Requests browser notification permission (once per browser).
 *  3. Obtains the FCM device token.
 *  4. POST /users/me/push-tokens  — registers the token with the backend.
 *  5. Listens for FOREGROUND messages and shows a toast notification.
 *  6. DELETE /users/me/push-tokens/{id}  — unregisters on logout.
 */
import { useEffect } from 'react'
import { getToken, onMessage } from 'firebase/messaging'
import { messaging } from '@/lib/firebase'
import { useAuthStore } from '@/store/auth'
import { toast } from '@/store/toast'
import api from '@/lib/api'

const VAPID_KEY     = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined
const SW_PATH       = '/firebase-messaging-sw.js'
const TOKEN_KEY     = 'fcm_token'      // raw FCM token string
const TOKEN_ID_KEY  = 'fcm_token_id'   // backend-assigned UUID for the push-token record

interface PushToken {
  id: string
  token: string
  platform: 'android' | 'ios' | 'web'
  device_name: string | null
  is_active: boolean
}

async function registerToken(token: string): Promise<PushToken> {
  const res = await api
    .post<{ success: true; data: PushToken }>('/users/me/push-tokens', {
      token,
      platform: 'web',
      device_name: navigator.userAgent.slice(0, 100),
    })
    .then((r) => r.data)
  return res.data
}

async function unregisterToken(id: string): Promise<void> {
  await api.delete(`/users/me/push-tokens/${id}`)
}

export function usePushNotifications() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  // ── Register on login ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !messaging || !VAPID_KEY) return

    let unsubscribeForeground: (() => void) | undefined

    async function init() {
      try {
        // 1. Register service worker
        const registration = await navigator.serviceWorker.register(SW_PATH)

        // 2. Request notification permission
        const permission = await Notification.requestPermission()
        if (permission !== 'granted') return

        // 3. Get FCM token
        const token = await getToken(messaging!, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        })
        if (!token) return

        // 4. Only call backend if token changed since last registration
        const storedToken = localStorage.getItem(TOKEN_KEY)
        if (token !== storedToken) {
          // Save token locally first so it survives even if backend call fails
          localStorage.setItem(TOKEN_KEY, token)
          try {
            const record = await registerToken(token)
            localStorage.setItem(TOKEN_ID_KEY, record.id)
          } catch (err) {
            // Backend endpoint may not be implemented yet — token is still
            // stored locally and Firebase can use it for direct sends
            console.warn('[FCM] Token registration with backend failed:', err)
          }
        }

        // 5. Foreground message handler — show toast while the tab is open
        unsubscribeForeground = onMessage(messaging!, (payload) => {
          const title = payload.notification?.title ?? payload.data?.title ?? 'Notification'
          const body  = payload.notification?.body  ?? payload.data?.body  ?? ''
          toast.info(body ? `${title} — ${body}` : title)
        })
      } catch (err) {
        // Non-fatal — push is a progressive enhancement
        console.warn('[FCM] Push setup failed:', err)
      }
    }

    void init()
    return () => { unsubscribeForeground?.() }
  }, [isAuthenticated])

  // ── Unregister on logout ─────────────────────────────────────────────────────
  useEffect(() => {
    if (isAuthenticated) return          // still logged in — nothing to do
    const id = localStorage.getItem(TOKEN_ID_KEY)
    if (!id) return

    unregisterToken(id)
      .catch(() => { /* ignore — token will expire naturally */ })
      .finally(() => {
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(TOKEN_ID_KEY)
      })
  }, [isAuthenticated])
}
